import { forwardRef, type TextareaHTMLAttributes } from "react";
import type { FulfillmentResourceMentionCandidate } from "../../types";
import { MentionTextarea, type MentionTarget } from "./MentionTextarea";
import "./stayRoomMentionInput.css";

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
  value: string; contextKey: string; active?: boolean;
  selectedCandidates?: readonly FulfillmentResourceMentionCandidate[];
  getDefaults?: (candidate: FulfillmentResourceMentionCandidate) => { formula: string; description: string; priceLabel: string; sourceLabel?: string };
  search: (query: string) => Promise<FulfillmentResourceMentionCandidate[]>;
  onValueChange: (text: string, candidate?: FulfillmentResourceMentionCandidate) => void;
};

/** 住宿名称允许尺寸与空格；完整、半截及全角计价均留在名称替换区外。 */
function stayMentionAt(input: HTMLTextAreaElement, selectedNames: readonly string[]): MentionTarget | null {
  if (input.selectionStart !== input.selectionEnd) return null;
  const text = input.value, caret = input.selectionStart;
  const start = Math.max(text.lastIndexOf("@", caret - 1), text.lastIndexOf("＠", caret - 1));
  if (start < 0 || (start > 0 && !/[\s，,；;]/.test(text[start - 1]))) return null;
  const tail = text.slice(start + 1);
  const selectedName = selectedNames.filter(name => tail.startsWith(name) && /^[ \t\u3000]+[0-9０-９]/.test(tail.slice(name.length))).sort((left, right) => right.length - left.length)[0];
  if (selectedName && caret > start + 1 + selectedName.length) return null;
  const boundary = tail.search(/[\n\r@＠#＃，,；;]/), segment = boundary < 0 ? tail : tail.slice(0, boundary);
  const formulaText = segment.replace(/[！-～]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xfee0));
  const complete = formulaText.search(/\s+\d+(?:\.\d+)?\s*间?\s*[×xX*]\s*\d+(?:\.\d+)?\s*晚?\s*[×xX*]\s*\d+(?:\.\d+)?\s*元?\s*$/);
  const partial = formulaText.search(/\s+(?=[\d.\s×xX*]*(?:间|晚))\d[\d.\s间晚元×xX*]*$/);
  const barePartial = formulaText.search(/\s+\d+(?:\.\d+)?\s*[×xX*]\s*(?:\d+(?:\.\d+)?\s*[×xX*]?\s*)?$/);
  const formula = [complete, partial, barePartial].filter(index => index >= 0).sort((left, right) => left - right)[0] ?? -1;
  const name = selectedName || (formula < 0 ? segment : segment.slice(0, formula)).trimEnd();
  const end = start + 1 + name.length;
  return caret < start + 1 || caret > end ? null : { start, end, query: text.slice(start + 1, caret).trim() };
}

/** 独立住宿房型选择；仅无计价内容时插入已确认的间数、晚数和参考价。 */
export const StayRoomMentionInput = forwardRef<HTMLTextAreaElement, Props>(function StayRoomMentionInput({ selectedCandidates = [], getDefaults, search, ...props }, ref) {
  return <MentionTextarea {...props} ref={ref} selectedNames={selectedCandidates.flatMap(candidate => candidate.skuName ? [candidate.skuName] : [])}
    hint={getDefaults ? "输入 @ 选择房型，带入默认计价后核对间数、晚数与单价；每行一项。" : "输入 @ 选择房型，再填间数 × 晚数 × 单价；每行一项。"}
    resourceLabel="房型" popupTitle="选择住宿房型" listLabel="住宿房型候选" getMention={stayMentionAt} getKey={candidate => candidate.skuName!} getName={candidate => candidate.skuName!} getLabel={candidate => candidate.skuName!}
    search={async query => {
      const names = new Set<string>();
      return (await search(query)).filter(candidate => {
        const name = candidate.skuName;
        if (candidate.resourceType !== "住宿" || candidate.level !== "sku" || !name?.trim() || names.has(name)) return false;
        names.add(name); return true;
      });
    }}
    renderCandidate={candidate => {
      const defaults = getDefaults?.(candidate);
      return defaults ? { content: <span className="confirmation-stay-mention-option-main"><strong>{candidate.skuName}</strong><span className="confirmation-stay-mention-option-price">{defaults.priceLabel}</span></span>, description: <span className="confirmation-stay-mention-option-details"><span>{defaults.description}</span>{defaults.sourceLabel ? <small>{defaults.sourceLabel}</small> : null}</span> } : undefined;
    }}
    replaceMention={(value, mention, candidate) => {
      const prefix = `${value.slice(0, mention.start)}@${candidate.skuName}`;
      const suffix = value.slice(mention.end), separator = /^[ \t]/.test(suffix) ? "" : " ";
      const boundary = suffix.search(/[\n\r@＠#＃，,；;]/), remainder = boundary < 0 ? suffix : suffix.slice(0, boundary);
      const formula = !remainder.trim() ? getDefaults?.(candidate).formula.trim() : "";
      const text = formula ? `${prefix} ${formula}${suffix.slice(remainder.length)}` : `${prefix}${separator}${suffix}`;
      const caret = prefix.length + 1;
      return { text, caret, selectionEnd: formula ? caret + (formula.match(/^[0-9０-９]+(?:[.．][0-9０-９]+)?/)?.[0].length || 0) : caret };
    }} />;
});
